import React, { useState, useEffect } from 'react';
import { localToday, dateStr } from '../utils/dates';
import { cleanNames } from '../utils/names';
import { auth, db } from '../firebase';
import { resolveMember } from '../utils/resolveMember';
import {
  collection, getDocs, query, where, doc, getDoc, updateDoc
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import { coachHours, dedupeSessions } from '../utils/coachHours';
import { getAllParentUids } from '../utils/athleteLinks';
import {
  Box, Typography, Paper, Grid, Chip, CircularProgress,
  Avatar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, Divider, Tabs, Tab,
  Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const InfoRow = ({ label, value, color }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 600, color: color || '#1E3A5F' }}>{value ?? '—'}</Typography>
  </Grid>
);

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const [y, mo, d] = dateStr.split('-');
  return new Date(y, mo - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const SESSION_LABELS = {
  'junior-practice': 'Junior Practice', 'junior-private': 'Junior Private',
  'junior-drop-in': 'Junior Drop-in', 'junior-evaluation': 'Junior Evaluation',
  'adult-practice': 'Adult Practice', 'league': 'League', 'practice': 'Practice', 'private': 'Private',
  'drop-in': 'Drop-in', 'evaluation': 'Evaluation', 'adult': 'Adult',
};

function CoachDashboard() {
  const [loading, setLoading] = useState(true);
  const [coachData, setCoachData] = useState(null);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [pastClasses, setPastClasses] = useState([]);
  const [sessionCounts, setSessionCounts] = useState({ head: 0, assistant: 0, private: 0 });
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [notifyDialog, setNotifyDialog] = useState(false);
  const [notifyClass, setNotifyClass] = useState(null);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifySending, setNotifySending] = useState(false);
  const [notifySent, setNotifySent] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '' });

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (!user) return;

      // Get user doc directly by UID
      // Canonical on both counts. A coach whose login was merged has an empty
      // pointer doc, so this used to load a blank profile into the edit form —
      // and handleSave then wrote that blank back to the same pointer doc,
      // where nothing reads it. The edit appeared to save and vanished.
      const { uid: canonUid, data: canonData } = await resolveMember(db, user);
      const userData = canonData || {};
      const coachMerged = { ...userData, id: canonUid };
      setCoachData(coachMerged);
      setForm({ firstName: coachMerged.firstName || '', lastName: coachMerged.lastName || '', phone: coachMerged.phone || '', email: coachMerged.email || '' });

      const today = localToday();
      const sevenDaysOut = new Date();
      sevenDaysOut.setDate(sevenDaysOut.getDate() + 7);
      const next7 = dateStr(sevenDaysOut);
      const coachId = user.uid;

      // Fetch head coach classes (no composite index needed)
      const headSnap = await getDocs(query(collection(db, 'classes'), where('headCoachId', '==', coachId)));
      const headClasses = headSnap.docs.map(d => ({ id: d.id, role: 'Head Coach', ...d.data() }))
        .filter(c => c.date >= today && c.date <= next7 && c.status !== 'canceled');

      // Fetch assistant classes — try with date filter, fall back without if index missing
      let assistClasses = [];
      try {
        const assistSnap = await getDocs(query(collection(db, 'classes'), where('assistantCoachIds', 'array-contains', coachId), where('date', '>=', today)));
        assistClasses = assistSnap.docs.map(d => ({ id: d.id, role: 'Assistant', ...d.data() }))
          .filter(c => c.date <= next7 && c.status !== 'canceled');
      } catch (e) {
        logError('CoachDashboard', e.message || String(e), e.stack || '');
        // Index not ready yet — fetch all and filter client-side
        const assistSnap = await getDocs(query(collection(db, 'classes'), where('assistantCoachIds', 'array-contains', coachId)));
        assistClasses = assistSnap.docs.map(d => ({ id: d.id, role: 'Assistant', ...d.data() }))
          .filter(c => c.date >= today && c.date <= next7 && c.status !== 'canceled');
      }


      // headClasses and assistClasses already defined above

      // Merge and deduplicate
      const allIds = new Set();
      // Deduped by session, not just by document id: a class covering two courts
      // is two documents, and listing both showed the coach the same practice
      // twice. See src/utils/coachHours.js.
      const merged = dedupeSessions([...headClasses, ...assistClasses].filter(c => {
        if (allIds.has(c.id)) return false;
        allIds.add(c.id);
        return c.status !== 'canceled';
      })).sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));

      setUpcomingClasses(merged);

      // Count past sessions by type
      const pastHeadSnap = await getDocs(query(collection(db, 'classes'), where('headCoachId', '==', coachId)));
      let pastAssistDocs = [];
      try {
        const pastAssistSnap = await getDocs(query(collection(db, 'classes'), where('assistantCoachIds', 'array-contains', coachId)));
        pastAssistDocs = pastAssistSnap.docs;
      } catch(e) { pastAssistDocs = []; }

      let headCount = 0, assistCount = 0, privateCount = 0;
      const calcHours = coachHours;   // per session, not per court document

      // Helper: a class is "past" only if its end time has passed.
      // Same-day classes still in progress or yet to start are NOT counted.
      const now = new Date();
      const isCompleted = (c) => {
        if (!c.date) return false;
        if (c.date < today) return true;        // any prior date → done
        if (c.date > today) return false;       // future date → not done
        // Same-day: compare end time
        if (!c.endTime) return false;
        const [eh, em] = c.endTime.split(':').map(Number);
        const classEnd = new Date();
        classEnd.setHours(eh, em, 0, 0);
        return now >= classEnd;
      };

      // Past head classes (status not canceled, end time has passed) — tagged with role
      const pastHead = pastHeadSnap.docs.map(d => ({ id: d.id, role: 'Head Coach', ...d.data() }))
        .filter(c => c.status !== 'canceled' && isCompleted(c));
      const pastAssist = pastAssistDocs.map(d => ({ id: d.id, role: 'Assistant', ...d.data() }))
        .filter(c => c.status !== 'canceled' && isCompleted(c));

      // Counted per session: two courts is one session coached, not two.
      const pastHeadSessions = dedupeSessions(pastHead);
      const pastAssistSessions = dedupeSessions(pastAssist);
      pastHeadSessions.forEach(c => {
        headCount++;
        if ((c.sessionType || '').toLowerCase().includes('private')) privateCount++;
      });
      pastAssistSessions.forEach(() => assistCount++);
      setSessionCounts({ head: headCount, assistant: assistCount, private: privateCount });

      // Pay calculation
      const headRate = parseFloat(userData.headRatePerHour) || 0;
      const asstRate = parseFloat(userData.assistantRatePerHour) || headRate;

      // Load coach payments
      const paySnap = await getDocs(query(collection(db, 'coachPayments'), where('coachId', '==', coachId)));
      const payments = paySnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.paidAt?.seconds || 0) - (a.paidAt?.seconds || 0));
      const totalPaid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

      // Determine the most recent payment date — anything after this is unpaid
      const lastPayment = payments[0];
      const lastPaidDate = lastPayment
        ? (lastPayment.datePaid || (lastPayment.paidAt?.seconds
            ? dateStr(new Date(lastPayment.paidAt.seconds * 1000))
            : ''))
        : '';

      // Past classes with payment status — sorted newest first
      // One row per session — each row carries `earned`, so a two-court class
      // listed twice showed (and totalled) double the pay.
      const allPast = [...pastHeadSessions, ...pastAssistSessions]
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      const pastClassesWithStatus = allPast.map(c => {
        const rate = c.role === 'Head Coach' ? headRate : asstRate;
        let hours = 1;
        if (c.startTime && c.endTime) {
          const [sh, sm] = c.startTime.split(':').map(Number);
          const [eh, em] = c.endTime.split(':').map(Number);
          hours = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
        }
        return {
          ...c,
          hours,
          earned: hours * rate,
          paid: lastPaidDate && c.date <= lastPaidDate,
        };
      });
      setPastClasses(pastClassesWithStatus);

      const totalOwed = (calcHours(pastHead) * headRate) + (calcHours(pastAssist) * asstRate);

      setCoachData(prev => ({ ...prev, totalOwed, totalPaid }));

      setLoading(false);
    };
    fetchData().catch(err => { console.error('CoachDashboard error:', err); setLoading(false); });
  }, []);

  const handleNotify = async () => {
    if (!notifyMsg.trim() || !notifyClass) return;
    setNotifySending(true);
    try {
      const coachName = `${coachData?.firstName || ''} ${coachData?.lastName || ''}`.trim();
      const classInfo = `${SESSION_LABELS[notifyClass.sessionType?.toLowerCase()] || notifyClass.sessionType}${notifyClass.skillGroup ? ' · ' + notifyClass.skillGroup : ''} — ${formatDate(notifyClass.date)}, ${formatTime(notifyClass.startTime)}${notifyClass.courtNumber ? ', Court ' + notifyClass.courtNumber : ''}`;

      // The roster for THIS class, from its attendance rows. The previous
      // version listed every athlete in the club and called it classAthletes —
      // which only failed to notify the entire club because the parent lookup
      // below never matched anyone.
      const attSnap = await getDocs(query(
        collection(db, 'attendance'), where('classId', '==', notifyClass.id)));
      const rosterAthleteIds = new Set(attSnap.docs
        .map(d => d.data())
        .filter(r => r.type !== 'coach')
        .map(r => r.athleteId)
        .filter(Boolean));
      if (rosterAthleteIds.size === 0) { setNotifySent(true); setNotifySending(false); return; }

      const athleteSnap = await getDocs(collection(db, 'athletes'));
      const rosterAthletes = athleteSnap.docs
        .filter(d => rosterAthleteIds.has(d.id))
        .map(d => ({ id: d.id, ...d.data() }));

      // Parents resolved through parentUserId / parentUserIds, like every other
      // screen. users.linkedAthletes is written [] at registration and nothing
      // has ever added to it, so targeting it meant no parent has received a
      // class notification since this was written.
      const parentUids = new Set();
      rosterAthletes.forEach(a => getAllParentUids(a).forEach(uid => parentUids.add(uid)));
      const selfUserIds = new Set(rosterAthletes.map(a => a.userId).filter(Boolean));
      const selfEmails = new Set(
        rosterAthletes.map(a => (a.email || '').toLowerCase()).filter(Boolean));

      const userSnap = await getDocs(collection(db, 'users'));
      const tokens = [];
      userSnap.forEach(doc => {
        const data = doc.data();
        if (!data.fcmTokens?.length) return;
        if (parentUids.has(doc.id)) { tokens.push(...data.fcmTokens); return; }
        if (selfUserIds.has(doc.id) || rosterAthleteIds.has(doc.id)
          || (data.email && selfEmails.has(data.email.toLowerCase()))) {
          tokens.push(...data.fcmTokens);
        }
      });

      if (tokens.length > 0) {
        const functions = getFunctions();
        const send = httpsCallable(functions, 'sendPushNotification');
        await send({ tokens, title: `Message from ${coachName} 🏐`, body: `${classInfo}
${notifyMsg.trim()}` });
      }
      setNotifySent(true);
    } catch (err) {
      logError('CoachDashboard', err.message || String(err), err.stack || '');
      console.error('Notify failed:', err);
    }
    setNotifySending(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // coachData.id is the canonical uid resolved on load — write there, not
      // to auth.currentUser.uid, which for a merged login is the pointer doc.
      await updateDoc(doc(db, 'users', coachData?.id || auth.currentUser.uid), { ...form, updatedAt: new Date() });
      // Same doc, second pass: normalises the name fields through cleanNames.
      if (coachData?.id) {
        await updateDoc(doc(db, 'users', coachData.id), cleanNames({ firstName: form.firstName, lastName: form.lastName, phone: form.phone, email: form.email }));
      }
      setCoachData(prev => ({ ...prev, ...form }));
      setEditing(false);
      setSuccess('Profile updated.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { console.error('Save failed:', err); logError('CoachDashboard', err.message || String(err), err.stack || ''); }
    setSaving(false);
  };

  if (loading) return (
    <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Layout>
  );

  const initials = (coachData?.firstName?.charAt(0) || '') + (coachData?.lastName?.charAt(0) || '');
  const totalOwed = coachData?.totalOwed || 0;
  const totalPaid = coachData?.totalPaid || 0;
  const balance = totalOwed - totalPaid;

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 860, mx: 'auto' }}>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 3 }}>
          <Avatar sx={{ width: 72, height: 72, backgroundColor: '#1E3A5F', fontSize: '1.6rem', flexShrink: 0 }}>
            {initials || coachData?.email?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
              {coachData?.firstName} {coachData?.lastName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#888' }}>{coachData?.email}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              {[
                (coachData?.roles || []).includes('coach') ? 'Head Coach' : null,
                (coachData?.roles || []).includes('assistant_coach') ? 'Asst Coach' : null,
                (coachData?.roles || []).includes('volunteer') ? 'Volunteer' : null,
              ].filter(Boolean).map(label => (
                <Chip key={label} label={label} size="small"
                  sx={{ backgroundColor: '#EBF5FB', color: '#1A5276', fontWeight: 600, fontSize: '0.75rem' }} />
              ))}
            </Box>
          </Box>
          {/* Balance summary */}
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', lineHeight: 1, color: balance > 0 ? '#27AE60' : '#888' }}>
              ${balance.toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>balance owed</Typography>
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            {!editing ? (
              <Button startIcon={<EditIcon />} onClick={() => setEditing(true)} variant="outlined" size="small"
                sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>Edit</Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} variant="contained" size="small"
                  sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#1E8449' } }}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button onClick={() => setEditing(false)} variant="outlined" size="small" sx={{ borderColor: '#888', color: '#888' }}>Cancel</Button>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper elevation={2} sx={{ borderRadius: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', px: 2 }}>
            <Tab label="My Account" />
            <Tab label="My Classes" />
            <Tab label="Past Sessions" />
          </Tabs>

          <Box sx={{ p: 3 }}>

            {/* ── TAB 0: My Account ── */}
            <TabPanel value={tab} index={0}>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>Session Counts</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <InfoRow label="Head Coach Sessions" value={sessionCounts.head} />
                <InfoRow label="Assistant Coach Sessions" value={sessionCounts.assistant} />
                <InfoRow label="Private Sessions" value={sessionCounts.private} />
              </Grid>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>Pay Information</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <InfoRow label="Head Coach Rate" value={coachData?.headRatePerHour ? `$${coachData.headRatePerHour}/hr` : null} />
                <InfoRow label="Assistant Rate" value={coachData?.assistantRatePerHour ? `$${coachData.assistantRatePerHour}/hr` : null} />
                <InfoRow label="Amount Owed" value={`$${(coachData?.totalOwed || 0).toFixed(2)}`} color="#1E3A5F" />
                <InfoRow label="Amount Paid" value={`$${(coachData?.totalPaid || 0).toFixed(2)}`} color="#27AE60" />
                <InfoRow label="Balance" value={`$${balance.toFixed(2)}`} color={balance > 0 ? '#E67E22' : '#888'} />
              </Grid>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>Contact Information</Typography>
              <Divider sx={{ mb: 2 }} />
              {editing ? (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="First Name" value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Last Name" value={form.lastName} onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} /></Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <InfoRow label="Email" value={coachData?.email} />
                  <InfoRow label="Phone" value={coachData?.phone} />
                </Grid>
              )}

            </TabPanel>

            {/* ── TAB 1: My Classes ── */}
            <TabPanel value={tab} index={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Upcoming Classes (Next 7 Days)</Typography>
              {upcomingClasses.length === 0 ? (
                <Alert severity="info">No upcoming classes assigned in the next 7 days.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Time</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Court</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Notify</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {upcomingClasses.map((c, i) => (
                        <TableRow key={c.id} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                          <TableCell>{formatDate(c.date)}</TableCell>
                          <TableCell>{formatTime(c.startTime)} – {formatTime(c.endTime)}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>
                            {SESSION_LABELS[c.sessionType?.toLowerCase()] || c.sessionType}
                            {c.skillGroup ? ` · ${c.skillGroup}` : ''}
                          </TableCell>
                          <TableCell>{c.courtNumber ? `Court ${c.courtNumber}` : '—'}</TableCell>
                          <TableCell>
                            <Chip label={c.role} size="small"
                              sx={{
                                backgroundColor: c.role === 'Head Coach' ? '#EBF5FB' : '#F4F6F7',
                                color: c.role === 'Head Coach' ? '#1A5276' : '#555',
                                fontWeight: 600, fontSize: '0.7rem'
                              }} />
                          </TableCell>
                          <TableCell align="center">
                            <Button size="small" variant="contained"
                              onClick={() => { setNotifyClass(c); setNotifyMsg(''); setNotifySent(false); setNotifyDialog(true); }}
                              sx={{ backgroundColor: '#1E3A5F', fontSize: '0.7rem', py: 0.3 }}>
                              Notify
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

            {/* ── TAB 2: Past Sessions ── */}
            <TabPanel value={tab} index={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Past Sessions Coached</Typography>
              {pastClasses.length === 0 ? (
                <Alert severity="info">No past sessions yet.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Time</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Hours</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Earned</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pastClasses.map((c, i) => (
                        <TableRow key={c.id} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                          <TableCell>{formatDate(c.date)}</TableCell>
                          <TableCell>{formatTime(c.startTime)} – {formatTime(c.endTime)}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>
                            {SESSION_LABELS[c.sessionType?.toLowerCase()] || c.sessionType}
                            {c.skillGroup ? ` · ${c.skillGroup}` : ''}
                          </TableCell>
                          <TableCell>
                            <Chip label={c.role} size="small"
                              sx={{
                                backgroundColor: c.role === 'Head Coach' ? '#EBF5FB' : '#F4F6F7',
                                color: c.role === 'Head Coach' ? '#1A5276' : '#555',
                                fontWeight: 600, fontSize: '0.7rem'
                              }} />
                          </TableCell>
                          <TableCell align="center">{c.hours.toFixed(1)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>${c.earned.toFixed(2)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={c.paid ? 'Paid' : 'Unpaid'}
                              size="small"
                              sx={{
                                backgroundColor: c.paid ? '#D5F5E3' : '#FADBD8',
                                color: c.paid ? '#1E8449' : '#922B21',
                                fontWeight: 700, fontSize: '0.7rem',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

          </Box>
        </Paper>
      </Box>
      {/* Notify Class Dialog */}
      <Dialog open={notifyDialog} onClose={() => { setNotifyDialog(false); setNotifySent(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>Notify Class</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {notifySent ? (
            <Alert severity="success">Message sent to class successfully!</Alert>
          ) : (
            <>
              {notifyClass && (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f0f4f8', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Sending to</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {SESSION_LABELS[notifyClass.sessionType?.toLowerCase()] || notifyClass.sessionType}{notifyClass.skillGroup ? ` · ${notifyClass.skillGroup}` : ''} — {formatDate(notifyClass.date)}, {formatTime(notifyClass.startTime)}{notifyClass.courtNumber ? `, Court ${notifyClass.courtNumber}` : ''}
                  </Typography>
                </Box>
              )}
              <TextField fullWidth multiline rows={4} label="Message *" value={notifyMsg}
                onChange={e => setNotifyMsg(e.target.value)}
                placeholder="e.g. Don't forget to bring water today!"
                inputProps={{ maxLength: 200 }}
                helperText={`${notifyMsg.length}/200`} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setNotifyDialog(false); setNotifySent(false); }} sx={{ color: '#888' }}>
            {notifySent ? 'Close' : 'Cancel'}
          </Button>
          {!notifySent && (
            <Button variant="contained" onClick={handleNotify} disabled={notifySending || !notifyMsg.trim()}
              sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>
              {notifySending ? 'Sending...' : 'Send Notification'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default CoachDashboard;
